import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface QRScannerProps {
  onScanSuccess: (decodedText: string) => void;
  onScanError?: (error: string) => void;
  darkMode: boolean;
}

export function QRScanner({ onScanSuccess, onScanError, darkMode }: QRScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null);
  const [cooldown, setCooldown] = useState(false);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScannedRef = useRef<string | null>(null);
  const cooldownTimeoutRef = useRef<number | null>(null);
  const scannerDivRef = useRef<HTMLDivElement>(null);
  
  // Safe check for iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    checkCameraPermission();
    return () => {
      if (cooldownTimeoutRef.current) {
        window.clearTimeout(cooldownTimeoutRef.current);
      }
      if (scannerRef.current && scanning) {
        scannerRef.current.stop();
      }
    };
  }, []);

  const checkCameraPermission = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(device => device.kind === 'videoinput');
      
      if (!hasCamera) {
        setCameraPermission(false);
        setError('No camera found on this device.');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment'
          } 
        });
        stream.getTracks().forEach(track => track.stop());
        setCameraPermission(true);
        setError(null);
        startScanner();
      } catch (err) {
        setCameraPermission(false);
        setError(isIOS 
          ? 'Camera access denied. Please enable camera access in your device settings.'
          : 'Camera access denied. Please grant camera permissions to scan QR codes.'
        );
      }
    } catch (err) {
      setCameraPermission(false);
      setError('Failed to access camera. Please check your device settings.');
    }
  };

  const handleRequestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { exact: 'environment' }
        } 
      });
      stream.getTracks().forEach(track => track.stop());
      setCameraPermission(true);
      setError(null);
      startScanner();
    } catch (err) {
      if (isIOS) {
        setError('Camera access denied. On iOS, please enable camera access in your device settings: Settings > Safari > Camera (or Settings > [App Name] > Camera)');
      } else {
        setError('Failed to access camera. Please grant camera permissions and try again.');
      }
      setCameraPermission(false);
    }
  };

  const startScanner = async () => {
    if (!scannerDivRef.current) return;

    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode('qr-reader');
      }

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await scannerRef.current.start(
        { facingMode: 'environment' },
        config,
        handleScan,
        (errorMessage: string) => {
          // Ignore "No QR code found" messages as they're expected
          if (!errorMessage.includes('No QR code found')) {
            console.error('QR Scanner error:', errorMessage);
            if (onScanError) onScanError(errorMessage);
          }
        }
      );
      
      setScanning(true);
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Failed to start camera scanner. Please try again.');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("qr-reader");
      }

      try {
        const result = await scannerRef.current.scanFile(file, true);
        if (result) {
          handleScan(result);
        }
      } catch (err) {
        console.error('Error scanning file:', err);
        setError('Could not read QR code from image. Please try another image or use the camera scanner.');
        if (onScanError) {
          onScanError('Failed to scan image');
        }
      }
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process image. Please try again.');
      if (onScanError) {
        onScanError('Failed to process image');
      }
    }

    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleScan = (decodedText: string) => {
    if (!decodedText || cooldown || decodedText === lastScannedRef.current) return;

    lastScannedRef.current = decodedText;
    setCooldown(true);
    onScanSuccess(decodedText);

    if (cooldownTimeoutRef.current) {
      window.clearTimeout(cooldownTimeoutRef.current);
    }

    cooldownTimeoutRef.current = window.setTimeout(() => {
      setCooldown(false);
      lastScannedRef.current = null;
    }, 3000);
  };

  return (
    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800 dark' : 'bg-white'} shadow`}>
      <div className="text-center mb-4">
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          QR Code Scanner
        </h3>
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Position the QR code within the frame to scan
        </p>
      </div>

      {/* Image Upload Button */}
      <div className="mb-4 text-center">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`px-4 py-2 rounded-md mb-4 ${
            darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
          }`}
        >
          📷 Scan from Image
        </button>
      </div>

      {cameraPermission === false ? (
        <div className="text-center">
          <div className="p-4 mb-4 bg-red-50 text-red-600 rounded-md">
            {isIOS ? (
              <>
                Camera access is required to scan QR codes. Please enable camera access in your device settings:
                <br />
                Settings &gt; Safari &gt; Camera
                <br />
                (or Settings &gt; [App Name] &gt; Camera)
              </>
            ) : (
              'Camera access is required to scan QR codes.'
            )}
          </div>
          <button
            onClick={handleRequestPermission}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Grant Camera Access
          </button>
        </div>
      ) : (
        <>
          <div ref={scannerDivRef} id="qr-reader" className="relative w-full max-w-md mx-auto" />

          {error && !error.includes('No QR code found') && (
            <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
              {error}
            </div>
          )}

          {cooldown && (
            <div className="mt-4 p-3 bg-blue-100 text-blue-800 rounded-md text-sm text-center">
              QR code scanned! Please wait a moment before scanning another code.
            </div>
          )}

          {!cooldown && scanning && (
            <div className="mt-4 p-3 bg-green-50 text-green-600 rounded-md text-sm text-center">
              Scanning...
            </div>
          )}
        </>
      )}
    </div>
  );
}